'use client';
import React, { Suspense } from 'react';
import LazyLoad from 'react-lazyload';
import { useConnatixLivestream } from '@benzinga/livestream-hooks';
import Hooks from '@benzinga/hooks';

const ConnatixVideoPlayer = React.lazy(() =>
  import('@benzinga/ads/connatix').then(module => {
    return { default: module.ConnatixVideoPlayer };
  }),
);

const ConnatixInitialHeadScript = React.lazy(() =>
  import('@benzinga/ads/connatix').then(module => {
    return { default: module.ConnatixInitialHeadScript };
  }),
);

export interface ConnatixLivestreamProps {
  /** Rendered while the live status is unknown, and when nothing else applies. */
  fallback?: React.ReactNode;
  /** Rendered when the stream is confirmed not live. */
  nonLive?: React.ReactNode;
}

const liveFallback = <div aria-hidden className="min-h-[170px] w-full" />;

/**
 * The Connatix livestream player without the CMS block registry. `ConnatixLiveBlock` wraps this
 * to render a CMS `non_live_widget`; import this one directly where no block fallback is needed
 * (e.g. the homepage), because lazy-importing `Block` ships every block type's CSS with the page.
 */
export const ConnatixLivestream: React.FC<ConnatixLivestreamProps> = ({ fallback, nonLive }) => {
  const { assembleLivestream, livestreamData } = useConnatixLivestream();
  const [isLive, setIsLive] = React.useState<boolean | null>(livestreamData ? true : null);

  Hooks.useEffectDidMount(() => {
    if (isLive !== null) {
      return;
    }

    let cancelled = false;
    assembleLivestream()
      .then(response => {
        if (!cancelled) {
          setIsLive(Boolean(response.enabled && response.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLive(false);
        }
      });

    return () => {
      cancelled = true;
    };
  });

  if (isLive === null) {
    return fallback ?? null;
  }

  if (isLive && livestreamData) {
    return (
      <div className="block my-4">
        <Suspense fallback={liveFallback}>
          <ConnatixInitialHeadScript id={livestreamData.id} />

          <LazyLoad offset={100} once>
            <ConnatixVideoPlayer {...livestreamData} />
          </LazyLoad>
        </Suspense>
      </div>
    );
  }

  if (!isLive && nonLive) {
    return nonLive;
  }

  if (isLive) {
    return fallback ?? liveFallback;
  }

  return fallback ?? null;
};
